import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMetaAdsGraphCampaigns } from "../../../hooks/metaAds/useMetaAdsSummary";
import { useMetaAdsPatchCampaignStatus } from "../../../hooks/metaAds/useMetaAdsPatchCampaignStatus";
import { useMetaAdsDeleteCampaign } from "../../../hooks/metaAds/useMetaAdsDeleteCampaign";
import { isMetaAdsApiConfigured } from "../../../hooks/metaAds/useMetaAdsApi";
import { isMetaAdsExperienceAvailable } from "../../../hooks/metaAds/useMetaAdsMode";
import {
  Breadcrumb,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Icon,
  Label,
} from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";
import MetaAdsInsightsModal from "./MetaAdsInsightsModal";
import { Switch } from "../../ui/UIElements/Switch";
import { cn } from "@/lib/utils";
import {
  metaAdsDestructiveIconButtonClass,
  metaAdsFormSectionClass,
  metaAdsFormSectionLabelClass,
  metaAdsGradientCtaClass,
  metaAdsTitleGradientClass,
} from "./metaAdsTheme";
import {
  formatMetaAdsAdSetOptimizationGoal,
  formatMetaAdsCampaignObjective,
  formatMetaAdsMoneyFromCents,
  formatMetaAdsRoas,
  metaAdsCampaignIsEffectivelyOn,
  metaAdsCampaignStatusCanToggle,
  META_ADS_DATE_PRESET_OPTIONS,
} from "./metaAdsUtils";

const MetaAdsPerformancePage = () => {
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const apiConfigured = isMetaAdsApiConfigured();
  const unlocked = isMetaAdsExperienceAvailable(apiConfigured);
  const [datePreset, setDatePreset] = useState("last_30d");

  const { data, isLoading, isError, error, refetch, isFetching } =
    useMetaAdsGraphCampaigns({
      enabled: Boolean(unlocked),
      datePreset,
    });

  const patchCampaignStatus = useMetaAdsPatchCampaignStatus();
  const deleteCampaignMutation = useMetaAdsDeleteCampaign();

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [insightsTarget, setInsightsTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("campaigns");

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        path: "/growth/meta-ads",
        icon: "Share2",
      },
      {
        label: t("performance.title"),
        active: true,
        disabled: true,
      },
    ],
    [t, tNav],
  );

  const campaigns = data?.campaigns ?? [];
  const adSets = data?.adSets ?? [];
  const ads = data?.ads ?? [];
  const activeRows =
    activeTab === "adSets" ? adSets : activeTab === "ads" ? ads : campaigns;
  const isEmpty =
    data && campaigns.length === 0 && adSets.length === 0 && ads.length === 0;
  const activeTabEmpty = data && activeRows.length === 0;

  const performanceTabs = useMemo(
    () => [
      {
        id: "campaigns",
        label: t("performance.tabCampaigns"),
        count: campaigns.length,
      },
      {
        id: "adSets",
        label: t("performance.tabAdSets"),
        count: adSets.length,
      },
      {
        id: "ads",
        label: t("performance.tabAds"),
        count: ads.length,
      },
    ],
    [adSets.length, ads.length, campaigns.length, t],
  );

  const ordersVal =
    data?.ordersAttributed != null ? data.ordersAttributed : null;
  const revenueVal = data?.revenueCents != null ? data.revenueCents : null;
  const roasVal = data?.roas != null ? data.roas : null;

  return (
    <div className="space-y-10">
      <Breadcrumb items={breadcrumbItems} />

      <MetaAdsInsightsModal
        datePreset={datePreset}
        entityId={insightsTarget?.id ?? ""}
        entityName={insightsTarget?.name ?? ""}
        entityType={insightsTarget?.entityType ?? "campaign"}
        isOpen={Boolean(insightsTarget)}
        onClose={() => setInsightsTarget(null)}
      />

      <DeleteConfirmationModal
        confirmButtonText={t("create.deleteCampaignConfirm")}
        confirmLoadingText={t("create.deleteCampaignDeleting")}
        detailNote={t("create.deleteCampaignIrreversible")}
        isLoading={deleteCampaignMutation.isPending}
        isOpen={Boolean(deleteTarget)}
        itemName={deleteTarget?.name || deleteTarget?.id || ""}
        itemType={t("create.deleteCampaignItemType")}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget?.id) {
            return;
          }
          deleteCampaignMutation.mutate(
            { campaignId: deleteTarget.id },
            { onSuccess: () => setDeleteTarget(null) },
          );
        }}
        subtitle={t("create.deleteCampaignModalSubtitle")}
        title={t("create.deleteCampaignModalTitle")}
      />

      {!unlocked && (
        <div
          className={cn(
            "rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100",
          )}
        >
          {t("hub.apiNotConfigured")}
        </div>
      )}

      <div className="space-y-3">
        <div className="mb-6 space-y-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              <span className={metaAdsTitleGradientClass}>
                {t("performance.titleGradientWord")}
              </span>
              <span className="text-foreground">
                {t("performance.titleRest")}
              </span>
            </h1>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              {t("performance.subtitle")}
            </p>
          </div>

          {/* Toolbar: BusinessFilters-style card; row layout at all widths to avoid stacked controls & empty space < sm */}
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-3 sm:p-4">
            <div className="flex w-full min-w-0 flex-row flex-wrap items-center justify-between gap-x-2 gap-y-2">
              <div className="flex max-w-full min-w-0 items-center gap-x-1.5 sm:gap-x-2">
                <Label
                  htmlFor="meta-performance-date-preset"
                  className={cn(
                    metaAdsFormSectionLabelClass,
                    "mb-0 shrink-0 whitespace-nowrap",
                  )}
                >
                  {t("performance.dateRangeLabel")}
                </Label>
                <span
                  className="shrink-0 text-white/30 select-none"
                  aria-hidden
                >
                  :
                </span>
                <select
                  id="meta-performance-date-preset"
                  value={datePreset}
                  onChange={(e) => {
                    setDatePreset(e.target.value);
                  }}
                  disabled={!unlocked}
                  aria-label={t("performance.dateRangeLabel")}
                  className={cn(
                    "custom-select h-10 w-[min(100%,12rem)] min-w-0 cursor-pointer rounded-md border border-gray-700 bg-gray-800 px-2 text-sm text-white sm:w-48 sm:min-w-[10rem] sm:px-3",
                    "shadow-sm transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {META_ADS_DATE_PRESET_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(`performance.${opt.i18nKey}`)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={!unlocked || isFetching}
                className={cn(
                  "inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md border border-gray-700 bg-gray-800 px-2.5 py-2 text-sm font-medium text-gray-200 transition-colors",
                  "sm:gap-2 sm:px-3",
                  "hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  !unlocked && "pointer-events-none opacity-50",
                )}
              >
                <Icon
                  name="RefreshCw"
                  className={cn("h-4 w-4", isFetching && "animate-spin")}
                  aria-hidden
                />
                {t("performance.refresh")}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {isLoading && unlocked && (
            <div className="space-y-6">
              <section className={metaAdsFormSectionClass}>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="bg-muted/30 border-border h-24 animate-pulse rounded-xl border"
                    />
                  ))}
                </div>
              </section>
              <section className={metaAdsFormSectionClass}>
                <div className="bg-muted/30 h-48 animate-pulse rounded-xl" />
              </section>
            </div>
          )}

          {isError && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error?.message || t("performance.error")}
            </div>
          )}

          {data && unlocked && !isEmpty && (
            <>
              <section className={metaAdsFormSectionClass}>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="border-border bg-muted/30 rounded-xl border p-4">
                    <p className={metaAdsFormSectionLabelClass}>
                      {t("performance.totalSpend")}
                    </p>
                    <p className="text-foreground mt-2 text-2xl font-semibold tabular-nums sm:text-3xl">
                      {formatMetaAdsMoneyFromCents(data.totalSpendCents)}
                    </p>
                  </div>
                  <div className="border-border bg-muted/30 rounded-xl border p-4">
                    <p className={metaAdsFormSectionLabelClass}>
                      {t("performance.orders")}
                    </p>
                    <p className="text-foreground mt-2 text-2xl font-semibold tabular-nums sm:text-3xl">
                      {ordersVal != null
                        ? ordersVal
                        : t("performance.comingSoon")}
                    </p>
                  </div>
                  <div className="border-border bg-muted/30 rounded-xl border p-4">
                    <p className={metaAdsFormSectionLabelClass}>
                      {t("performance.revenue")}
                    </p>
                    <p className="text-foreground mt-2 text-2xl font-semibold tabular-nums sm:text-3xl">
                      {revenueVal != null
                        ? formatMetaAdsMoneyFromCents(revenueVal)
                        : t("performance.comingSoon")}
                    </p>
                  </div>
                  <div className="border-border bg-muted/30 rounded-xl border p-4">
                    <p className={metaAdsFormSectionLabelClass}>
                      {t("performance.returnLabel")}
                    </p>
                    <p className="text-foreground mt-2 text-2xl font-semibold tabular-nums sm:text-3xl">
                      {roasVal != null
                        ? formatMetaAdsRoas(roasVal)
                        : t("performance.comingSoon")}
                    </p>
                  </div>
                </div>
              </section>

              {/* <section className={metaAdsFormSectionClass}>
                <h2 className={metaAdsFormSectionLabelClass}>
                  {t("performance.chartTitle")}
                </h2>
                <div className="from-primary/20 mt-4 flex h-48 items-end gap-2 rounded-lg bg-gradient-to-t to-transparent px-2 pt-8 pb-0">
                  {[35, 52, 48, 61, 55, 70, 66].map((h, i) => (
                    <div
                      key={i}
                      className="from-primary to-brand-cyan/80 flex-1 rounded-t-md bg-gradient-to-t"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <p className="text-muted-foreground mt-2 text-center text-xs">
                  {t("performance.chartPlaceholder")}
                </p>
              </section> */}

              <section className={metaAdsFormSectionClass}>
                <div
                  className="border-border/70 bg-muted/20 mb-4 flex flex-wrap gap-2 rounded-xl border p-1.5"
                  role="tablist"
                  aria-label={t("performance.tabsAriaLabel")}
                >
                  {performanceTabs.map((tab) => {
                    const selected = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        className={cn(
                          "inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors sm:flex-none sm:justify-start",
                          selected
                            ? "from-primary/90 to-brand-cyan/90 text-primary-foreground bg-gradient-to-r shadow-sm"
                            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                        )}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        <span>{tab.label}</span>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs tabular-nums",
                            selected
                              ? "bg-black/20 text-white"
                              : "bg-background/70 text-muted-foreground",
                          )}
                        >
                          {tab.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead className="border-border bg-muted/30 text-muted-foreground border-b">
                      {activeTab === "campaigns" && (
                        <tr>
                          <th
                            className="text-muted-foreground w-24 min-w-[5.5rem] px-2 py-3 text-center text-xs font-medium"
                            scope="col"
                          >
                            {t("performance.colActive")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colCampaign")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colStatus")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colObjective")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colSpend")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colImpressions")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colClicks")}
                          </th>
                          <th className="text-muted-foreground w-14 min-w-[3.5rem] px-2 py-3 text-center text-xs font-medium">
                            {t("performance.colActions")}
                          </th>
                        </tr>
                      )}
                      {activeTab === "adSets" && (
                        <tr>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colAdSet")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colCampaign")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colStatus")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colOptimizationGoal")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colDailyBudget")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colSpend")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colImpressions")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colClicks")}
                          </th>
                        </tr>
                      )}
                      {activeTab === "ads" && (
                        <tr>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colAd")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colAdSet")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colCampaign")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colStatus")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colCreative")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colSpend")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colImpressions")}
                          </th>
                          <th className="px-4 py-3 font-medium">
                            {t("performance.colClicks")}
                          </th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {activeTabEmpty && (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-muted-foreground px-4 py-8 text-center"
                          >
                            {activeTab === "adSets"
                              ? t("performance.emptyAdSets")
                              : activeTab === "ads"
                                ? t("performance.emptyAds")
                                : t("performance.empty")}
                          </td>
                        </tr>
                      )}

                      {activeTab === "campaigns" &&
                        campaigns.map((c) => {
                          const canToggle = metaAdsCampaignStatusCanToggle(
                            c.status,
                          );
                          const isOn = metaAdsCampaignIsEffectivelyOn(c.status);
                          const toggling =
                            patchCampaignStatus.isPending &&
                            patchCampaignStatus.variables?.campaignId ===
                              c.campaignId;
                          const deletingThis =
                            deleteCampaignMutation.isPending &&
                            deleteTarget?.id === String(c.campaignId);
                          const editCampaignHref = `/growth/meta-ads/edit/${encodeURIComponent(
                            String(c.campaignId),
                          )}`;

                          return (
                            <tr
                              key={String(c.campaignId)}
                              className="border-border/60 border-b last:border-0"
                            >
                              <td className="text-foreground/90 w-24 min-w-[5.5rem] px-2 py-3 text-center align-middle">
                                <div
                                  className="flex min-h-5 items-center justify-center gap-1.5"
                                  role={toggling ? "status" : undefined}
                                  aria-live={toggling ? "polite" : undefined}
                                >
                                  {toggling ? (
                                    <>
                                      <Icon
                                        name="Loader2"
                                        className="text-muted-foreground h-4 w-4 shrink-0 animate-spin"
                                        aria-hidden
                                      />
                                      <span className="sr-only">
                                        {t(
                                          "performance.campaignStatusUpdating",
                                        )}
                                      </span>
                                    </>
                                  ) : null}
                                  <Switch
                                    checked={isOn}
                                    disabled={
                                      !unlocked ||
                                      !canToggle ||
                                      Boolean(toggling)
                                    }
                                    onCheckedChange={(next) => {
                                      patchCampaignStatus.mutate({
                                        campaignId: c.campaignId,
                                        status: next ? "ACTIVE" : "PAUSED",
                                        datePreset,
                                      });
                                    }}
                                    aria-label={t(
                                      "performance.campaignStatusToggleAria",
                                      {
                                        name:
                                          c.internalName ||
                                          String(c.campaignId),
                                      },
                                    )}
                                  />
                                </div>
                              </td>
                              <td className="text-foreground px-4 py-3 align-middle">
                                {!unlocked ? (
                                  <Link
                                    to={editCampaignHref}
                                    className="text-foreground hover:text-primary inline font-medium"
                                  >
                                    {c.internalName || c.campaignId}
                                  </Link>
                                ) : (
                                  <HoverCard openDelay={80} closeDelay={200}>
                                    <HoverCardTrigger asChild>
                                      <div className="block w-full max-w-xl text-left">
                                        <Link
                                          to={editCampaignHref}
                                          className="text-foreground hover:text-primary inline font-medium"
                                        >
                                          {c.internalName || c.campaignId}
                                        </Link>
                                      </div>
                                    </HoverCardTrigger>
                                    <HoverCardContent
                                      side="bottom"
                                      align="start"
                                      sideOffset={8}
                                      collisionPadding={12}
                                      className="border-border bg-card text-foreground z-[60] w-fit min-w-[11rem] rounded-md border p-2 shadow-lg"
                                    >
                                      <div className="flex flex-col gap-0.5">
                                        <Link
                                          to={editCampaignHref}
                                          className="text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex w-full items-center gap-2 rounded px-2 py-1 text-xs font-medium transition-colors"
                                          aria-label={t(
                                            "performance.campaignRowEditAria",
                                            {
                                              name:
                                                c.internalName ||
                                                String(c.campaignId),
                                            },
                                          )}
                                        >
                                          <Icon
                                            name="Pencil"
                                            className="h-3.5 w-3.5 shrink-0"
                                            aria-hidden
                                          />
                                          {t("performance.campaignRowEdit")}
                                        </Link>
                                        <button
                                          type="button"
                                          disabled={!unlocked}
                                          className="text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                          aria-label={t(
                                            "performance.campaignRowInsightsAria",
                                            {
                                              name:
                                                c.internalName ||
                                                String(c.campaignId),
                                            },
                                          )}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setInsightsTarget({
                                              entityType: "campaign",
                                              id: String(c.campaignId),
                                              name:
                                                c.internalName ||
                                                String(c.campaignId),
                                            });
                                          }}
                                        >
                                          <Icon
                                            name="LineChart"
                                            className="h-3.5 w-3.5 shrink-0"
                                            aria-hidden
                                          />
                                          {t("performance.rowViewCharts")}
                                        </button>
                                      </div>
                                    </HoverCardContent>
                                  </HoverCard>
                                )}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {c.status || "—"}
                              </td>
                              <td className="text-foreground/80 px-4 py-3">
                                {formatMetaAdsCampaignObjective(c.objective, t)}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {formatMetaAdsMoneyFromCents(c.spendCentsSum)}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {c.impressionsSum ?? "—"}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {c.clicksSum ?? "—"}
                              </td>
                              <td className="text-foreground/90 w-14 px-2 py-3 text-center align-middle">
                                <button
                                  type="button"
                                  disabled={
                                    !unlocked ||
                                    Boolean(deletingThis) ||
                                    Boolean(toggling)
                                  }
                                  onClick={() =>
                                    setDeleteTarget({
                                      id: String(c.campaignId),
                                      name:
                                        c.internalName || String(c.campaignId),
                                    })
                                  }
                                  aria-label={t(
                                    "performance.deleteCampaignRowAria",
                                    {
                                      name:
                                        c.internalName || String(c.campaignId),
                                    },
                                  )}
                                  className={metaAdsDestructiveIconButtonClass}
                                >
                                  {deletingThis ? (
                                    <Icon
                                      name="Loader2"
                                      className="text-muted-foreground h-4 w-4 animate-spin"
                                      aria-hidden
                                    />
                                  ) : (
                                    <Icon
                                      name="Trash2"
                                      className="h-4 w-4"
                                      aria-hidden
                                    />
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                      {activeTab === "adSets" &&
                        adSets.map((adSet) => {
                          const editAdSetHref = `/growth/meta-ads/ad-sets/edit/${encodeURIComponent(
                            String(adSet.adSetId),
                          )}`;

                          return (
                            <tr
                              key={String(adSet.adSetId)}
                              className="border-border/60 border-b last:border-0"
                            >
                              <td className="text-foreground px-4 py-3 align-middle font-medium">
                                <HoverCard openDelay={80} closeDelay={200}>
                                  <HoverCardTrigger asChild>
                                    <div className="block w-full max-w-xl text-left">
                                      <Link
                                        to={editAdSetHref}
                                        className="text-foreground hover:text-primary inline font-medium"
                                      >
                                        {adSet.internalName || adSet.adSetId}
                                      </Link>
                                    </div>
                                  </HoverCardTrigger>
                                  <HoverCardContent
                                    side="bottom"
                                    align="start"
                                    sideOffset={8}
                                    collisionPadding={12}
                                    className="border-border bg-card text-foreground z-[60] w-fit min-w-[11rem] rounded-md border p-2 shadow-lg"
                                  >
                                    <div className="flex flex-col gap-0.5">
                                      <Link
                                        to={editAdSetHref}
                                        className="text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex w-full items-center gap-2 rounded px-2 py-1 text-xs font-medium transition-colors"
                                        aria-label={t(
                                          "performance.adSetRowEditAria",
                                          {
                                            name:
                                              adSet.internalName ||
                                              String(adSet.adSetId),
                                          },
                                        )}
                                      >
                                        <Icon
                                          name="Pencil"
                                          className="h-3.5 w-3.5 shrink-0"
                                          aria-hidden
                                        />
                                        {t("performance.adSetRowEdit")}
                                      </Link>
                                      <button
                                        type="button"
                                        disabled={!unlocked}
                                        className="text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label={t(
                                          "performance.adSetRowInsightsAria",
                                          {
                                            name:
                                              adSet.internalName ||
                                              String(adSet.adSetId),
                                          },
                                        )}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setInsightsTarget({
                                            entityType: "adset",
                                            id: String(adSet.adSetId),
                                            name:
                                              adSet.internalName ||
                                              String(adSet.adSetId),
                                          });
                                        }}
                                      >
                                        <Icon
                                          name="LineChart"
                                          className="h-3.5 w-3.5 shrink-0"
                                          aria-hidden
                                        />
                                        {t("performance.rowViewCharts")}
                                      </button>
                                    </div>
                                  </HoverCardContent>
                                </HoverCard>
                              </td>
                              <td className="text-foreground/80 px-4 py-3">
                                {adSet.campaignName ||
                                  adSet.campaignId ||
                                  t("performance.comingSoon")}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {adSet.status || "—"}
                              </td>
                              <td className="text-foreground/80 px-4 py-3">
                                {formatMetaAdsAdSetOptimizationGoal(
                                  adSet.optimizationGoal,
                                  t,
                                )}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {adSet.dailyBudgetCents != null
                                  ? formatMetaAdsMoneyFromCents(
                                      adSet.dailyBudgetCents,
                                    )
                                  : "—"}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {formatMetaAdsMoneyFromCents(
                                  adSet.spendCentsSum,
                                )}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {adSet.impressionsSum ?? "—"}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {adSet.clicksSum ?? "—"}
                              </td>
                            </tr>
                          );
                        })}

                      {activeTab === "ads" &&
                        ads.map((ad) => {
                          const editAdHref = `/growth/meta-ads/ads/edit/${encodeURIComponent(
                            String(ad.adId),
                          )}`;

                          return (
                            <tr
                              key={String(ad.adId)}
                              className="border-border/60 border-b last:border-0"
                            >
                              <td className="text-foreground px-4 py-3 align-middle font-medium">
                                <HoverCard openDelay={80} closeDelay={200}>
                                  <HoverCardTrigger asChild>
                                    <div className="block w-full max-w-xl text-left">
                                      <Link
                                        to={editAdHref}
                                        className="text-foreground hover:text-primary inline font-medium"
                                      >
                                        {ad.internalName || ad.adId}
                                      </Link>
                                    </div>
                                  </HoverCardTrigger>
                                  <HoverCardContent
                                    side="bottom"
                                    align="start"
                                    sideOffset={8}
                                    collisionPadding={12}
                                    className="border-border bg-card text-foreground z-[60] w-fit min-w-[11rem] rounded-md border p-2 shadow-lg"
                                  >
                                    <div className="flex flex-col gap-0.5">
                                      <Link
                                        to={editAdHref}
                                        className="text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex w-full items-center gap-2 rounded px-2 py-1 text-xs font-medium transition-colors"
                                        aria-label={t(
                                          "performance.adRowEditAria",
                                          {
                                            name:
                                              ad.internalName ||
                                              String(ad.adId),
                                          },
                                        )}
                                      >
                                        <Icon
                                          name="Pencil"
                                          className="h-3.5 w-3.5 shrink-0"
                                          aria-hidden
                                        />
                                        {t("performance.adRowEdit")}
                                      </Link>
                                      <button
                                        type="button"
                                        disabled={!unlocked}
                                        className="text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label={t(
                                          "performance.adRowInsightsAria",
                                          {
                                            name:
                                              ad.internalName ||
                                              String(ad.adId),
                                          },
                                        )}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setInsightsTarget({
                                            entityType: "ad",
                                            id: String(ad.adId),
                                            name:
                                              ad.internalName ||
                                              String(ad.adId),
                                          });
                                        }}
                                      >
                                        <Icon
                                          name="LineChart"
                                          className="h-3.5 w-3.5 shrink-0"
                                          aria-hidden
                                        />
                                        {t("performance.rowViewCharts")}
                                      </button>
                                    </div>
                                  </HoverCardContent>
                                </HoverCard>
                              </td>
                              <td className="text-foreground/80 px-4 py-3">
                                {ad.adSetName ||
                                  ad.adSetId ||
                                  t("performance.comingSoon")}
                              </td>
                              <td className="text-foreground/80 px-4 py-3">
                                {ad.campaignName ||
                                  ad.campaignId ||
                                  t("performance.comingSoon")}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {ad.status || "—"}
                              </td>
                              <td className="text-foreground/80 px-4 py-3">
                                {ad.creativeId || "—"}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {formatMetaAdsMoneyFromCents(ad.spendCentsSum)}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {ad.impressionsSum ?? "—"}
                              </td>
                              <td className="text-foreground/90 px-4 py-3">
                                {ad.clicksSum ?? "—"}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                    {isFetching && !isLoading && (
                      <tfoot>
                        <tr className="border-border/60 border-t">
                          <td
                            colSpan={8}
                            className="bg-muted/10 px-4 py-3 text-center align-middle"
                          >
                            <div
                              role="status"
                              aria-live="polite"
                              aria-label={t("performance.loading")}
                              className={cn(
                                "border-border/60 bg-muted/20 text-muted-foreground inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm",
                              )}
                            >
                              <Icon
                                name="Loader2"
                                className="text-muted-foreground h-4 w-4 shrink-0 animate-spin"
                                aria-hidden
                              />
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </section>
            </>
          )}

          {data && unlocked && isEmpty && (
            <section className={metaAdsFormSectionClass}>
              <div className="flex flex-col items-center py-6 sm:py-10">
                <div className="border-border bg-muted/30 mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border">
                  <Icon
                    name="BarChart3"
                    className="text-muted-foreground h-10 w-10"
                    aria-hidden
                  />
                </div>
                <h2 className="text-foreground text-xl font-bold">
                  {t("performance.emptyTitle")}
                </h2>
                <p className="text-muted-foreground mx-auto mt-2 text-center text-sm">
                  {t("performance.emptyBody")}
                </p>
                <Link
                  to="/growth/meta-ads/create"
                  className={cn(
                    "mt-8 inline-flex h-12 items-center justify-center rounded-xl px-8 text-base font-semibold",
                    metaAdsGradientCtaClass,
                  )}
                >
                  {t("performance.emptyCta")}
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaAdsPerformancePage;
